import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { updateCloudinaryUrl, transformVideoPath } from "./data/helper";
import { AppState } from "@Store/index";
import { useEffect, useRef, useState } from "react";
import styles from "./ReviewsSectionStyles.module.scss";
import CuratedReviews from "./Components/CuratedReviews";
import ReviewCard from "./Components/ReviewCard";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import Button from "@Components/Button";
import PropertyDetailsModal from "../PropertyDetailsModal";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import SectionContainer from "../SectionContainer";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import WhiteStarIcon from "@Icons/WhiteStarIcon";
import HorizontalScroll from "@Components/HorizontalScroll";
import RadioButton from "@Components/Radio";
import Modal from "@Components/Modal";
import SortIcon from "@Icons/SortIcon";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import AddReview from "./Components/AddReview";
// import Plus from "@Icons/Plus";
import useAddReviewModal from "hooks/useAddReviewModal";
import ReviewMediaThumbnail from "./Components/ReviewMediaThumbnail";
import SectionHeader from "./Components/SectionHeader";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import StarRating from "@Components/StarRating";
import Portal from "@Components/Portal";
import useAppPadding from "hooks/useAppPadding";
import useIsApp from "hooks/useIsApp";
//

const ReviewsSection = ({ isDesktop = false }) => {
  const { reviews_count, reviews_rating, review_summary, reviews } = useSelector(
    ({ propertyPageData }: AppState) => ({
      id: propertyPageData?.data?.id,
      reviews_count: propertyPageData?.inventoryReviewData?.length || 0,
      reviews_rating: propertyPageData?.data?.reviews_rating,
      review_summary: propertyPageData?.data?.meta?.review_summary,
      reviews: propertyPageData?.inventoryReviewData,
    }),
    shallowEqual,
  );

  const { isAbove } = useResponsiveness();
  const appPadding = useAppPadding();
  const isApp = useIsApp();

  let reviewsWithVideoIndexes =
    reviews?.map((rev: any) => {
      const videos: any[] = [];
      const images: any[] = [];

      // Process media array and separate into videos and images
      rev?.media?.forEach((mediaItem: any, idx: number) => {
        if (mediaItem?.thumbnail_url) {
          // Has thumbnail - add to videos array
          videos.push({
            ...mediaItem,
            index: idx,
            platform: "gumlet",
          });
        } else {
          // No thumbnail - add to images array
          images.push({
            ...mediaItem,
            index: idx,
          });
        }
      });

      return {
        ...rev,
        videos,
        images,
      };
    }) || [];
  reviewsWithVideoIndexes = reviewsWithVideoIndexes?.sort(
    (a: any, b: any) => b?.rating - a?.rating,
  );

  // Add Review modal
  const [showOuterModal, setShowOuterModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  useAddReviewModal(modalRef, () => setShowOuterModal(false), showOuterModal);

  let queryParams: any = null;
  if (typeof window !== "undefined") {
    queryParams = new URLSearchParams(window.location.search);
  }
  const [showModal, setShowModal]: [any, any] = useState(false);
  const [modalReviews, setModalReviews]: [any, any] = useState(reviewsWithVideoIndexes);
  const [reviewFilter, setReviewFilter] = useState("");
  const [showSortFilter, setShowSortFilter] = useState(false);
  const [hideOverflow, setHideOverflow]: any = useState();
  const [showReviewCollection, setShowReviewCollection] = useState(false);

  useEffect(() => {
    document.addEventListener("click", (e: any) => {
      if (
        !e?.target?.closest("#sortFilterDropdown") &&
        e?.target?.id != "sortButton" &&
        !e?.target?.closest("#sortButton") &&
        isDesktop
      ) {
        setShowSortFilter(false);
      }
    });

    const review_collection = queryParams?.get("review_collection");
    if (review_collection) {
      setShowReviewCollection(review_collection === "true");
    }
  }, []);

  // useEffect to handle review change
  const handleFilterChange = (filter: string, closeModal = true) => {
    setReviewFilter(filter);
    switch (filter) {
      case "Newly Added":
        setModalReviews((mr: any) =>
          // @ts-ignore
          [...mr.sort((a: any, b: any) => new Date(b.timestamp) - new Date(a.timestamp))],
        );
        break;
      case "Low to High":
        setModalReviews((mr: any) => [...mr.sort((a: any, b: any) => a.rating - b.rating)]);
        break;
      case "High to Low":
        setModalReviews((mr: any) => [...mr.sort((a: any, b: any) => b.rating - a.rating)]);
        break;
      default:
        setModalReviews([...reviewsWithVideoIndexes]);
        break;
    }
    setHideOverflow(false);
    if (closeModal) setShowSortFilter(false);
  };
  // Function to calculate the difference in years and months between two dates
  function yearMonthDiff(date1: Date, date2: Date) {
    let years = date2.getFullYear() - date1.getFullYear();
    let months = date2.getMonth() - date1.getMonth();

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return { years, months };
  }
  const handleModalOpen = () => {
    gtagEventEmitter({
      action: "viewed_reviews",
      category: PAGE.INVENTORY,
      label: "success",
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setReviewFilter("");
    handleFilterChange("");
  };
  const getTimeText = (review: any) => {
    // get date obj from timestamp
    const Date1: any = new Date(review.timestamp);
    const Date2: any = new Date();

    const timeDifferenceMs = Date2 - Date1;

    // Convert the difference to days, hours, minutes, and seconds
    const millisecondsInASecond = 1000;
    const millisecondsInAMinute = millisecondsInASecond * 60;
    const millisecondsInAnHour = millisecondsInAMinute * 60;
    const millisecondsInADay = millisecondsInAnHour * 24;

    const yearsDifference = yearMonthDiff(Date1, Date2);

    const daysDifference = Math.floor(timeDifferenceMs / millisecondsInADay);

    let timeText = "";
    if (yearsDifference.years) {
      timeText = `${yearsDifference.years} year${yearsDifference.years > 1 ? "s" : ""} ago`;
    } else if (yearsDifference.months) {
      timeText = `${yearsDifference.months} month${yearsDifference.months > 1 ? "s" : ""} ago`;
    } else if (daysDifference) {
      timeText = `${daysDifference} day${daysDifference > 1 ? "s" : ""} ago`;
    }
    return timeText;
  };

  const mediaArray: any = [];
  reviewsWithVideoIndexes?.forEach((rev: any) => {
    rev?.videos?.forEach((revVideo: any) => {
      mediaArray.push({
        thumbnail_url: updateCloudinaryUrl(transformVideoPath(revVideo?.thumbnail_url), 200, 600),
        path: transformVideoPath(revVideo?.path),
        caption: rev?.user_details?.name,
        index: mediaArray.length,
        platform: "gumlet",
        rating: rev?.rating,
        timeText: getTimeText(rev),
        category: "videos",
      });
      revVideo["index"] = mediaArray.length - 1;
    });
  });
  reviewsWithVideoIndexes?.forEach((rev: any) => {
    rev?.images?.forEach((revImg: any) => {
      mediaArray.push({
        base_path: revImg?.path,
        path: revImg?.path,
        caption: rev?.user_details?.name,
        index: mediaArray.length,
        rating: rev?.rating,
        timeText: getTimeText(rev),
        category: "images",
      });
      revImg["index"] = mediaArray.length - 1;
    });
  });
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const handleMediaClick = (index: number, gtagAction: string) => {
    gtagEventEmitter({
      action: gtagAction,
      category: PAGE.INVENTORY,
      label: "",
    });
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: mediaArray,
      reviewSection: true,
      currentItemIndex: index,
      showViewRoomButton: false,
    });
  };

  const { t } = useTranslationHook();

  useEffect(() => {
    if (!(reviews_count > 0 || review_summary)) {
      const event = new Event("navigation_section_removed");
      if (typeof window !== "undefined") {
        window.dispatchEvent(event);
      }
    }
  }, [reviews_count, review_summary]);

  if (!(reviews_count > 0 || review_summary)) {
    return <>{showReviewCollection && <AddReview isDesktop={isDesktop} />}</>;
  }

  return (
    <>
      {(reviews_count > 0 || review_summary) && (
        <>
          <SectionContainer
            title={
              <SectionHeader
                rating={reviews_rating}
                reviewCount={reviews_count}
                isDesktop={isDesktop}
                t={t}
              />
            }
            id="reviews"
            className={formatClassNames(styles.container, isDesktop ? styles.desktop : "")}
            isDesktop={isDesktop}
            headingLevel={2}
          >
            {reviews_count > 0 && isDesktop && (
              <StarRating
                rating={Number(reviews_rating)}
                reviewCount={reviews_count}
                className={styles.starRatingPill}
                testId="propertyPage-reviewsSection-starRating"
              />
            )}
            {review_summary?.summary && (
              <CuratedReviews
                hasReviews={reviews_count > 0}
                isDesktop={isDesktop && isAbove(SCREEN_SIZE.SM)}
                reviewSummary={review_summary}
              />
            )}

            {reviews_count ? (
              <div className={styles.reviewBottomPart}>
                <div className={styles.reviewHeaderContainer}>
                  {review_summary && (
                    <div className={styles.reviewSubSectionTitle}>
                      {`${reviews_count} ${t(
                        reviews_count === 1
                          ? "propertypage.reviewssection.reviewtitle"
                          : "propertypage.reviewssection.reviewstitle",
                      )}`}
                    </div>
                  )}
                  {mediaArray.length ? (
                    <div className={styles.reviewsWithMedia}>
                      <HorizontalScroll
                        className={formatClassNames(
                          styles.horizontalScroll,
                          isDesktop && styles.desktop,
                        )}
                      >
                        {mediaArray.map((media: any, idx: any) => (
                          <ReviewMediaThumbnail
                            key={media.category === "videos" ? `videoDiv${idx}` : `imgs${idx}`}
                            media={media}
                            isDesktop={isDesktop}
                            idx={idx}
                            handleMediaClick={handleMediaClick}
                          />
                        ))}
                      </HorizontalScroll>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
                <HorizontalScroll
                  showControls={isDesktop}
                  itemSize={468}
                  className={formatClassNames(styles.horizontalScroll, isDesktop && styles.desktop)}
                >
                  {reviewsWithVideoIndexes?.slice(0, 4).map((review: any, idx: any) => (
                    <ReviewCard
                      getTimeText={getTimeText}
                      review={review}
                      handleMediaClick={handleMediaClick}
                      key={`ReviewCard${idx}`}
                      isDesktop={isDesktop}
                      reviews={reviews}
                    />
                  ))}
                </HorizontalScroll>
                <div className={formatClassNames(styles.divider, isDesktop && styles.desktop)} />
                <Button
                  variant="showAll"
                  className={styles.readAllReviews}
                  labelStyles={styles.readAllReviewsWrapper}
                  onClick={handleModalOpen}
                  isDesktop={isDesktop}
                >
                  {t("propertypage.reviewssection.readallreviewstext")}
                </Button>
                {showModal && (
                  <>
                    {isDesktop ? (
                      <PropertyDetailsModal
                        header={
                          <div
                            className={formatClassNames(
                              styles.reviewSectionTitle,
                              isDesktop && styles.desktop,
                            )}
                          >
                            {reviews_rating && (
                              <div className={styles.ratingDiv}>
                                <span>{reviews_rating}</span> <WhiteStarIcon size="16" />
                              </div>
                            )}
                            {t("propertypage.reviewssection.reviewstitle")}
                          </div>
                        }
                        hidePropertyModal={handleModalClose}
                        reducedZIndex
                        isDesktop={isDesktop}
                      >
                        <div>
                          {mediaArray.length ? (
                            <div className={styles.reviewsWithMedia}>
                              <HorizontalScroll
                                className={formatClassNames(
                                  styles.horizontalScroll,
                                  isDesktop && styles.desktop,
                                  styles.insideModal,
                                )}
                              >
                                {mediaArray.map((media: any, idx: any) => (
                                  <ReviewMediaThumbnail
                                    key={
                                      media.category === "videos" ? `videoDiv${idx}` : `imgs${idx}`
                                    }
                                    media={media}
                                    isDesktop={isDesktop}
                                    idx={idx}
                                    handleMediaClick={handleMediaClick}
                                  />
                                ))}
                              </HorizontalScroll>
                            </div>
                          ) : (
                            <></>
                          )}
                          <h2 className={styles.sectionTitle2}>
                            <span className={styles.titleSmallText}>
                              {`${t("propertypage.reviewssection.alltext")} ${reviews_count} ${t(
                                reviews_count === 1
                                  ? "propertypage.reviewssection.reviewtitle"
                                  : "propertypage.reviewssection.reviewstitle",
                              )}`}
                            </span>
                            <button
                              type="button"
                              className={styles.sortButton}
                              onClick={() => setShowSortFilter(true)}
                              id="sortButton"
                            >
                              <SortIcon fill="#000000" />
                              {t("propertypage.reviewssection.sort")}
                            </button>
                            {showSortFilter && (
                              <div className={styles.sortFilterDropdown} id="sortFilterDropdown">
                                <div className={styles.sortFilterHeader}>
                                  {t("propertypage.reviewssection.sort")}
                                </div>
                                <div
                                  className={styles.filterOption}
                                  role="none"
                                  onClick={() => {
                                    handleFilterChange("Newly Added");
                                  }}
                                >
                                  <RadioButton isSelected={reviewFilter == "Newly Added"} />
                                  <span>
                                    {t("propertypage.reviewssection.sortoptionnewlyadded")}
                                  </span>
                                </div>
                                <div
                                  className={styles.filterOption}
                                  role="none"
                                  onClick={() => {
                                    handleFilterChange("Low to High");
                                  }}
                                >
                                  <RadioButton isSelected={reviewFilter == "Low to High"} />
                                  <span>{t("propertypage.reviewssection.ratinglowtohigh")}</span>
                                </div>
                                <div
                                  className={styles.filterOption}
                                  role="none"
                                  onClick={() => {
                                    handleFilterChange("High to Low");
                                  }}
                                >
                                  <RadioButton isSelected={reviewFilter == "High to Low"} />
                                  <span>{t("propertypage.reviewssection.ratinghightolow")}</span>
                                </div>
                              </div>
                            )}
                          </h2>
                          {modalReviews?.map((review: any, idx: any) => (
                            <ReviewCard
                              showAsList
                              getTimeText={getTimeText}
                              review={review}
                              handleMediaClick={handleMediaClick}
                              handleModalClose={handleModalClose}
                              key={`ReviewCard${idx}`}
                              isDesktop={isDesktop}
                              reviews={modalReviews}
                            />
                          ))}
                        </div>
                      </PropertyDetailsModal>
                    ) : (
                      <Modal
                        hideModal={handleModalClose}
                        // reducedZIndex
                        // isDesktop={isDesktop}
                        hideOverflow={hideOverflow}
                        isHistoryPush={!isDesktop}
                      >
                        <div>
                          {isApp && appPadding > 0 && (
                            <div
                              style={{
                                height: `${appPadding}px`,
                                background: "#ffffff",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <div
                            className={formatClassNames(
                              styles.reviewSectionTitle,
                              styles.desktop,
                              styles.modalTitle,
                            )}
                          >
                            {reviews_rating && (
                              <div className={styles.ratingDiv}>
                                <span>{reviews_rating}</span> <WhiteStarIcon size="16" />
                              </div>
                            )}
                            {t("propertypage.reviewssection.reviewstitle")}
                          </div>
                          <div className={styles.modalReviewsContainermWeb}>
                            {mediaArray.length ? (
                              <div className={styles.reviewsWithMedia}>
                                <HorizontalScroll
                                  className={formatClassNames(
                                    styles.horizontalScroll,
                                    isDesktop && styles.desktop,
                                    styles.insideModal,
                                  )}
                                >
                                  {mediaArray.map((media: any, idx: any) => (
                                    <ReviewMediaThumbnail
                                      key={
                                        media.category === "videos"
                                          ? `videoDiv${idx}`
                                          : `imgs${idx}`
                                      }
                                      media={media}
                                      isDesktop={isDesktop}
                                      idx={idx}
                                      handleMediaClick={handleMediaClick}
                                    />
                                  ))}
                                </HorizontalScroll>
                              </div>
                            ) : (
                              <></>
                            )}
                            <h2 className={styles.sectionTitle2}>
                              <span className={styles.titleSmallText}>
                                {`${t("All")} ${reviews_count} ${t(
                                  reviews_count === 1 ? "review" : "reviews",
                                )}`}
                              </span>
                              <button
                                type="button"
                                className={styles.sortButton}
                                onClick={() => {
                                  setShowSortFilter(true);
                                  setHideOverflow(true);
                                }}
                                id="sortButton"
                              >
                                <SortIcon fill="#000000" />
                                {t("propertypage.reviewssection.sort")}
                              </button>
                            </h2>
                            {showSortFilter && (
                              <Portal>
                                <PropertyDetailsModal
                                  actionButtonText={t("propertypage.reviewssection.applybutton")}
                                  actionButtonClick={() => {
                                    handleFilterChange(reviewFilter);
                                  }}
                                  showResetButton
                                  disableReset={!reviewFilter.length}
                                  disableActionButton={!reviewFilter.length}
                                  resetButtonClick={() => {
                                    handleFilterChange("", false);
                                  }}
                                  header="propertypage.reviewssection.sortbyheader"
                                  hidePropertyModal={() => {
                                    handleFilterChange("");
                                  }}
                                >
                                  <div
                                    className={styles.filterOption}
                                    role="none"
                                    onClick={() => {
                                      setReviewFilter("Newly Added");
                                    }}
                                  >
                                    <RadioButton isSelected={reviewFilter == "Newly Added"} />
                                    <span>
                                      {t("propertypage.reviewssection.sortoptionnewlyadded")}
                                    </span>
                                  </div>
                                  <div
                                    className={styles.filterOption}
                                    role="none"
                                    onClick={() => {
                                      setReviewFilter("Low to High");
                                    }}
                                  >
                                    <RadioButton isSelected={reviewFilter == "Low to High"} />
                                    <span>{t("propertypage.reviewssection.ratinglowtohigh")}</span>
                                  </div>
                                  <div
                                    className={styles.filterOption}
                                    role="none"
                                    onClick={() => {
                                      setReviewFilter("High to Low");
                                    }}
                                  >
                                    <RadioButton isSelected={reviewFilter == "High to Low"} />
                                    <span>{t("propertypage.reviewssection.ratinghightolow")}</span>
                                  </div>
                                </PropertyDetailsModal>
                              </Portal>
                            )}
                            {modalReviews?.map((review: any, idx: any) => (
                              <ReviewCard
                                showAsList
                                getTimeText={getTimeText}
                                review={review}
                                handleMediaClick={handleMediaClick}
                                handleModalClose={handleModalClose}
                                key={`ReviewCard${idx}`}
                                reviews={modalReviews}
                                isDesktop={isDesktop}
                              />
                            ))}
                          </div>
                        </div>
                      </Modal>
                    )}
                  </>
                )}
              </div>
            ) : (
              <></>
            )}
          </SectionContainer>
        </>
      )}
      {showReviewCollection && <AddReview isDesktop={isDesktop} />}
    </>
  );
};

export default wrapperHOC(ReviewsSection, {
  componentName: "ReviewsSection-PropertyPage",
  showForChina: true,
});
